import DefaultLayout from '../layouts/defaultLayout';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Switch,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText
} from '@mui/material';
import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from '@emotion/styled';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { showToast } from '../utils/toast';
import uploadFilesToFirebaseStorage from '../firebase/storage';
import { db } from '../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import MediaInput from '../components/MediaInput';
import { IS_DEMO } from '../utils/config';

const StyledSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 82,
  height: 40,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(42px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        opacity: 1,
        border: 0
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600]
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 35,
    height: 35
  },
  '& .MuiSwitch-track': {
    borderRadius: 40 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500
    })
  }
}));

const tabs = ['Audio', 'Storybook', 'Breath'];
const categories = [
  'Select Category',
  'Story',
  'Meditation',
  'Mindfulness',
  'Music',
  'Sound',
  'Lullaby'
];

const defaultFormValues = {
  title: '',
  description: '',
  tab: tabs[0],
  category: categories[0],
  status: false,
  listImage: null,
  bannerImage: null,
  sectionImage: null,
  audio: null,
  bgVideo: null,
  pages: null,
  video: null
};

/**
 * Helper function to upload a media value to Firebase Storage
 * If the value is already a URL string, it returns the URL directly
 * If the value is a File object, it uploads to Firebase Storage
 */
const uploadMedia = async (value) => {
  if (!value) return '';
  // If it's already a URL string, return it directly
  if (typeof value === 'string') {
    return value;
  }
  // Otherwise, upload to Firebase Storage
  return await uploadFilesToFirebaseStorage(value);
};

const createContent = async (content) => {
  await addDoc(collection(db, 'posts'), content);
  showToast({
    type: 'success',
    message: 'New post published.'
  });
};

export default function AddContents({ formValues = defaultFormValues, onUpload = createContent }) {
  const [tab, setTab] = useState(formValues.tab);
  const [title, setTitle] = useState(formValues.title);
  const [description, setDescription] = useState(formValues.description);
  const [category, setCategory] = useState(formValues.category);
  const [status, setStatus] = useState(formValues.status);

  // Images
  const [listImage, setListImage] = useState(formValues.listImage);
  const [bannerImage, setBannerImage] = useState(formValues.bannerImage);
  const [sectionImage, setSectionImage] = useState(formValues.sectionImage);

  const [progress, setProgress] = useState('');

  // Audio
  const [audio, setAudio] = useState(formValues.audio);
  const [bgVideo, setBgVideo] = useState(formValues.bgVideo);
  // Storybook
  const [pages, setPages] = useState(formValues.pages);
  // Breath
  const [video, setVideo] = useState(formValues.video);

  const navigate = useNavigate();

  async function onClickUpload() {
    // Demo constraint
    if (IS_DEMO) {
      return showToast({
        type: 'error',
        message: 'This is a demo version. You cannot add new content.'
      });
    }

    
    

    const content = {
      dateCreated: Timestamp.now(),
      title: title,
      description: description,
      visible: true,
      paid: status,
      banner: true,
      type: tab,
      category: tab === 'Audio' ? category : tab,
      media: {
        listImage: '',
        bannerImage: '',
        newSectionImage: '',
        audio: '',
        bgVideo: '',
        pages: [],
        video: ''
      },
      search: [status ? 'paid' : 'unpaid']
    };

    if (title === '') {
      return showToast({
        type: 'error',
        message: 'Add a title.'
      });
    }
    if (!listImage || !bannerImage || !sectionImage) {
      return showToast({
        type: 'error',
        message: 'Select List Image, Banner Image, New Section Image.'
      });
    }

    if (tab === 'Audio') {
      if (category === categories[0]) {
        return showToast({
          type: 'error',
          message: 'Select at least one category'
        });
      }
      if (!audio || !bgVideo) {
        return showToast({
          type: 'error',
          message: 'Select a Audio and Background Video.'
        });
      }
      setProgress('Uploading audio file');
      content.media.audio = await uploadMedia(audio);
      setProgress('Uploading Background Video');
      content.media.bgVideo = await uploadMedia(bgVideo);
    }

    if (tab === 'Storybook') {
      if (!pages || pages.length === 0) {
        return showToast({
          type: 'error',
          message: 'Add at least one page.'
        });
      }

      const pagesLinks = [];

      setProgress('Uploading Pages');

      for (let index = 0; index < pages.length; index++) {
        const link = await uploadMedia(pages[index]);
        pagesLinks.push(link);
      }

      content.media.pages = pagesLinks;
    }
    if (tab === 'Breath') {
      if (!video) {
        return showToast({
          type: 'error',
          message: 'Select a Video.'
        });
      }

      setProgress('Uploading Background Video');
      content.media.video = await uploadMedia(video);
    }

    setProgress('Uploading list Image');
    content.media.listImage = await uploadMedia(listImage);
    setProgress('Uploading banner Image');
    content.media.bannerImage = await uploadMedia(bannerImage);
    setProgress('Uploading section Image');
    content.media.newSectionImage = await uploadMedia(sectionImage);

    

    function _replace(val) {
      return val
        .toLowerCase()
        .replaceAll('-', ' ')
        .replaceAll('(', '')
        .replaceAll(')', '')
        .replaceAll('?', '')
        .replaceAll('/', ' ')
        .replaceAll('  ', ' ');
    }

    content.search.push(..._replace(content.title).split(' '), tab.toLowerCase());

    if (tab === 'Audio') {
      content.search.push(category.toLowerCase());
    }

    try {
      onUpload(content);
      setProgress('Uploaded.');
      navigate('/');
    } catch (error) {
      showToast({
        type: 'error',
        message: error
      });
    }
  }

  /**
   * Helper function to get preview URL for a value (File or URL string)
   */
  const getPreviewUrl = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    try {
      return URL.createObjectURL(value);
    } catch {
      return null;
    }
  };

  return (
    <DefaultLayout>
      <Dialog
        maxWidth="md"
        fullWidth={true}
        open={progress !== ''}
        onClose={() => {}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{'In progress'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ py: 5 }} id="alert-dialog-description">
            {progress}
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Box display="flex" flexDirection="column" gap={3}>
        <Typography color="primary.main" variant="h4" fontWeight={400}>
          Add file
        </Typography>
        <Box color="common.white" maxWidth="900px">
          <Tabs
            textColor="inherit"
            variant="fullWidth"
            value={tab}
            onChange={(_, value) => setTab(value)}>
            {tabs.map((value, index) => (
              <Tab
                sx={{
                  backgroundColor: value === tab ? 'primary.main' : '#C1C1C1',
                  marginRight: index + 1 === tabs.length ? 0 : 3
                }}
                value={value}
                label={value}
                key={value}
              />
            ))}
          </Tabs>
        </Box>
        <Box maxWidth="590px" display="flex" flexDirection="column" gap={2}>
          <Typography variant="body1">Title</Typography>
          <TextField value={title} onChange={(e) => setTitle(e.target.value)} size="small" />
        </Box>
        <Box maxWidth="590px" display="flex" flexDirection="column" gap={2}>
          <Typography variant="body1">Description</Typography>
          <TextField
            value={description}
            multiline
            onChange={(e) => setDescription(e.target.value)}
            size="small"
          />
        </Box>
        {/* Image inputs using MediaInput component */}
        <Box flexWrap="wrap" display="flex" alignItems="flex-start" gap={4}>
          <Box maxWidth="250px">
            <MediaInput
              value={listImage}
              onChange={setListImage}
              accept="image/*"
              label="List image"
              mediaType="image"
            />
          </Box>
          <Box maxWidth="250px">
            <MediaInput
              value={bannerImage}
              onChange={setBannerImage}
              accept="image/*"
              label="Banner image"
              mediaType="image"
            />
          </Box>
          <Box maxWidth="250px">
            <MediaInput
              value={sectionImage}
              onChange={setSectionImage}
              accept="image/*"
              label="New section image"
              mediaType="image"
            />
          </Box>
        </Box>
        {tab === 'Audio' && (
          <>
            <Box maxWidth="590px">
              <MediaInput
                value={audio}
                onChange={setAudio}
                accept="audio/*"
                label="Audio"
                mediaType="audio"
              />
            </Box>
            <Box maxWidth="590px">
              <MediaInput
                value={bgVideo}
                onChange={setBgVideo}
                accept="video/*"
                label="Background video"
                mediaType="video"
              />
            </Box>
          </>
        )}
        {tab === 'Breath' && (
          <Box maxWidth="590px">
            <MediaInput
              value={video}
              onChange={setVideo}
              accept="video/*"
              label="Video"
              mediaType="video"
            />
          </Box>
        )}
        {tab === 'Storybook' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1">Pages</Typography>
            <Box maxWidth="590px" display="flex" alignItems="center" gap={2}>
              <Button fullWidth sx={{ textAlign: 'start' }} variant="outlined" component="label">
                <input
                  multiple
                  accept="image/*"
                  type="file"
                  onChange={(e) =>
                    setPages((prev) =>
                      prev
                        ? [...Array.from(e.target.files), ...prev]
                        : [...Array.from(e.target.files)]
                    )
                  }
                  hidden
                />
                Choose files
              </Button>
              <IconButton onClick={() => setPages(null)}>
                <DeleteIcon />
              </IconButton>
            </Box>
            {/* Pages URL input */}
            <Box maxWidth="590px">
              <TextField
                fullWidth
                size="small"
                placeholder="Or paste image URLs (comma-separated)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const urls = e.target.value
                      .split(',')
                      .map((url) => url.trim())
                      .filter((url) => url);
                    if (urls.length > 0) {
                      setPages((prev) => (prev ? [...urls, ...prev] : urls));
                      e.target.value = '';
                    }
                  }
                }}
                helperText="Press Enter to add URLs"
              />
            </Box>
            {pages && (
              <Box flexWrap="wrap" display="flex" alignItems="center" gap={3}>
                {pages.map((src, index) => {
                  const previewUrl = getPreviewUrl(src);
                  return (
                    <Box key={index} position="relative">
                      <Button
                        component="label"
                        sx={{
                          height: '125px',
                          width: '125px',
                          padding: 0
                        }}>
                        {previewUrl && (
                          <img
                            style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                            src={previewUrl}
                            alt={`page ${index + 1}`}
                          />
                        )}
                        <input
                          accept="image/*"
                          type="file"
                          onChange={(e) =>
                            setPages((prev) =>
                              [...prev].map((value, _index) =>
                                _index === index ? e.target.files[0] : value
                              )
                            )
                          }
                          hidden
                        />
                      </Button>
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'error.main',
                          color: 'white',
                          '&:hover': { backgroundColor: 'error.dark' }
                        }}
                        onClick={() => setPages((prev) => prev.filter((_, i) => i !== index))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
        {['Audio'].includes(tab) && (
          <Box maxWidth="590px" display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1">Category</Typography>
            <Select onChange={(e) => setCategory(e.target.value)} value={category} fullWidth>
              {categories.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body1">Status ( {status ? 'Paid' : 'Unpaid'} )</Typography>
          <StyledSwitch
            onChange={() => setStatus((prevState) => !prevState)}
            checked={status}
            size="large"
          />
        </Box>
        <Box display="flex" alignItems="center" gap={3}>
          <Button
            sx={{ color: 'white' }}
            color="success"
            size="large"
            variant="contained"
            onClick={onClickUpload}>
            Upload
          </Button>
          <Button color="error" size="large" variant="contained">
            Cancel
          </Button>
        </Box>
      </Box>
    </DefaultLayout>
  );
}
